import React, { useEffect, useState } from 'react'
import { RefreshControl } from 'react-native'
import { ArrowLeft, Store, Users, ShoppingCart, MapPin, Calendar, ChevronRight, Printer } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import { Badge, BadgeText, Card, ListItem, Spinner, StatCard, StatusBadge, Text, TopBar, XStack, Theme, YStack, ScrollView } from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MarketAdmin'>
  route: RouteProp<RootStackParamList, 'MarketAdmin'>
}
interface MarketStats {
  flea_market_id: string; name: string; invite_code: string; commission_percentage: number
  status: string; active_sellers: number; pending_sellers: number
  products_for_sale: number; products_sold: number; total_sales: number
}
interface Market {
  id: string; name: string; description: string; address_street: string | null
  address_city: string | null; invite_code: string; commission_percentage: number
  allow_self_registration: boolean; status: string
}

export default function MarketAdminScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t } = useLanguage()
  const [market, setMarket] = useState<Market | null>(null)
  const [stats, setStats] = useState<MarketStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => { fetchMarketData() }, [marketId])

  const fetchMarketData = async () => {
    try {
      const { data: marketData } = await supabase.from('flea_markets').select('*').eq('id', marketId).single()
      setMarket(marketData)
      const { data: statsData } = await supabase.from('market_dashboard_stats').select('*').eq('flea_market_id', marketId).single()
      setStats(statsData)
    } catch (e) { console.error('Error fetching market:', e) }
    finally { setLoading(false); setRefreshing(false) }
  }

  if (loading) return <YStack f={1} jc="center" ai="center" bg="$background"><Spinner size="large" /></YStack>
  if (!market) return <YStack f={1} jc="center" ai="center" bg="$background"><Text tone="secondary">{t('market_not_found') || 'Market not found'}</Text></YStack>

  const actionIcon = (Icon: any) => (
    <YStack w={44} h={44} br="$md" bg="rgba(192,122,95,0.1)" jc="center" ai="center"><Icon size={22} color="#C07A5F" /></YStack>
  )

  return (
    <Theme name="brick">
      <YStack f={1} bg="$background">
        <TopBar
          title={market.name}
          leftIcon={<ArrowLeft size={18} color="#C07A5F" />}
          onLeftPress={() => navigation.navigate('MerchantStoreSelect')}
          leftLabel={t('switch_store') || 'Switch'}
        />
        <ScrollView f={1} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchMarketData() }} />}>
          <XStack flexWrap="wrap" gap="$sm" px="$md" mt="$md" mb="$md">
            <StatCard minWidth="45%" f={1} label={t('active_sellers') || 'Active Sellers'} value={String(stats?.active_sellers || 0)} />
            <StatCard minWidth="45%" f={1} label={t('pending') || 'Pending'} value={String(stats?.pending_sellers || 0)} />
            <StatCard minWidth="45%" f={1} label={t('for_sale') || 'For Sale'} value={String(stats?.products_for_sale || 0)} />
            <StatCard minWidth="45%" f={1} label={t('sold') || 'Sold'} value={String(stats?.products_sold || 0)} />
          </XStack>

          <Card bg="#C07A5F" br="$xl" mx="$md" mb="$lg" p="$lg" ai="center">
            <Text color="rgba(255,255,255,0.8)" fontSize={14}>{t('total_sales') || 'Total Sales'}</Text>
            <Text color="#fff" fontSize={36} fontWeight="700" mt={4}>€{(stats?.total_sales || 0).toFixed(2)}</Text>
          </Card>

          <YStack px="$md" mb="$lg">
            <Text preset="caption" tone="muted" mb="$sm" textTransform="uppercase" letterSpacing={0.5}>
              {t('management') || 'Management'}
            </Text>
            {[
              { icon: Store, title: t('manage_spaces') || 'Manage Spaces', sub: t('spaces_hint') || 'Tables, booths, and racks', nav: () => navigation.navigate('MarketSpaces', { marketId }) },
              { icon: Users, title: t('manage_sellers') || 'Manage Sellers', sub: stats?.pending_sellers ? `${stats.pending_sellers} ${t('pending_applications') || 'pending'}` : (t('sellers_hint') || 'Approve and manage sellers'), right: stats?.pending_sellers ? <Badge variant="warning"><BadgeText>{stats.pending_sellers}</BadgeText></Badge> : undefined, nav: () => navigation.navigate('MarketSellers', { marketId }) },
              { icon: Calendar, title: t('view_reservations') || 'View Reservations', sub: t('reservations_hint') || 'Space bookings and schedules', nav: () => navigation.navigate('SpaceReservations', { marketId }) },
              { icon: ShoppingCart, title: t('point_of_sale') || 'Point of Sale', sub: t('pos_hint') || 'Record sales and checkout', nav: () => navigation.navigate('MarketPOS', { marketId }), extra: { borderWidth: 2, borderColor: '$success' } },
              { icon: Printer, title: t('print_tags') || 'Print Tags', sub: t('print_tags_hint') || 'Print price tags for products', nav: () => navigation.navigate('MarketPrintTags', { marketId }) },
            ].map((item, i) => (
              <ListItem
                key={i}
                icon={actionIcon(item.icon)}
                title={item.title}
                subtitle={item.sub}
                right={item.right || <ChevronRight size={20} color="#ccc" />}
                onPress={item.nav}
                bg="$backgroundCard" br="$lg" p="$md" mb="$xs"
                {...(item.extra || {})}
              />
            ))}
          </YStack>

          <Card variant="elevated" br="$xl" mx="$md" p="$md" mb="$2xl">
            <Text preset="caption" tone="muted" mb="$sm" textTransform="uppercase" letterSpacing={0.5}>
              {t('market_info') || 'Market Info'}
            </Text>
            {market.description && <Text preset="body" mb="$xs">{market.description}</Text>}
            {(market.address_street || market.address_city) && (
              <XStack ai="center" mb="$xs" gap={4}>
                <MapPin size={14} color="#858B80" />
                <Text preset="bodySmall" tone="secondary">{[market.address_street, market.address_city].filter(Boolean).join(', ')}</Text>
              </XStack>
            )}
            <XStack ai="center" mt="$xs" gap="$sm">
              <Text preset="bodySmall" tone="secondary">{t('status') || 'Status'}:</Text>
              <StatusBadge label={t(market.status) || market.status} variant={market.status === 'active' ? 'success' : 'default'} />
            </XStack>
          </Card>

          <YStack h={80} />
        </ScrollView>
      </YStack>
    </Theme>
  )
}
